type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

// En desarrollo, usar localhost:8060. En producción, usar ruta relativa
const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8060' 
  : '' // Ruta relativa - el navegador usará el mismo dominio

const TOKEN_KEY = 'auth_token'
const USER_TYPE_KEY = 'user_type_id'
const USER_NAME_KEY = 'user_name'

// Clase de error personalizada para errores HTTP
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public originalMessage?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Función helper para obtener mensaje de error amigable
function getFriendlyErrorMessage(status: number, message: string): string {
  // Si el backend ya proporciona un mensaje claro, usarlo
  if (message && message !== `HTTP ${status}`) {
    return message
  }

  // Mensajes amigables por código HTTP
  switch (status) {
    case 400:
      return 'La solicitud no es válida. Por favor, verifica los datos ingresados.'
    case 401:
      return 'No autorizado. Por favor, inicia sesión nuevamente.'
    case 403:
      return 'No tienes permisos para realizar esta acción.'
    case 404:
      return 'El recurso solicitado no fue encontrado.'
    case 409:
      return 'Ya existe un recurso con estos datos.'
    case 422:
      return 'Los datos proporcionados no son válidos.'
    case 429:
      return 'Demasiadas solicitudes. Por favor, intenta más tarde.'
    case 500:
      return 'Error interno del servidor. Por favor, intenta más tarde o contacta al administrador.'
    case 502:
    case 503:
    case 504:
      return 'El servidor no está disponible. Por favor, intenta más tarde.'
    default:
      return `Error del servidor (${status}). Por favor, intenta nuevamente.`
  }
}

export function setToken(t: string) { localStorage.setItem(TOKEN_KEY, t) }
export function getToken(): string | null { return localStorage.getItem(TOKEN_KEY) }
export function clearToken() { localStorage.removeItem(TOKEN_KEY) }

export function setUserType(ut: string) { localStorage.setItem(USER_TYPE_KEY, ut) }
export function getUserType(): string | null { return localStorage.getItem(USER_TYPE_KEY) }
export function clearUserType() { localStorage.removeItem(USER_TYPE_KEY) }

export function setUserName(name: string) { localStorage.setItem(USER_NAME_KEY, name) }
export function getUserName(): string | null { return localStorage.getItem(USER_NAME_KEY) }
export function clearUserName() { localStorage.removeItem(USER_NAME_KEY) }

async function request(path: string, method: HttpMethod, body?: any) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: (() => {
      const h: Record<string,string> = { 'Content-Type': 'application/json' }
      const t = getToken()
      if (t) h['Authorization'] = `Bearer ${t}`
      return h
    })(),
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    let errorMessage = `HTTP ${res.status}`
    let originalMessage: string | undefined
    
    try {
      const data = await res.json()
      if (data.error) {
        originalMessage = data.error
        errorMessage = data.error
      }
    } catch {
      // Si no se puede parsear JSON, usar el mensaje por defecto
    }

    // Si es error 401 y tenemos token, limpiar token (sesión expirada)
    if (res.status === 401 && getToken()) {
      clearToken()
      clearUserType()
      clearUserName()
    }

    const friendlyMessage = getFriendlyErrorMessage(res.status, errorMessage)
    throw new ApiError(res.status, friendlyMessage, originalMessage)
  }
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) return res.json()
  return res.text()
}

export const api = {
  get: (path: string) => request(path, 'GET'),
  post: (path: string, body?: any) => request(path, 'POST', body),
  put: (path: string, body?: any) => request(path, 'PUT', body),
  delete: (path: string) => request(path, 'DELETE'),
}

// Función helper para manejar errores de forma consistente
// Retorna el mensaje de error apropiado para mostrar al usuario
export function handleApiError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message
  }
  
  if (error instanceof Error) {
    return error.message
  }
  
  return 'Ocurrió un error inesperado. Por favor, intenta nuevamente.'
}

// Función helper para determinar si un error requiere redirección
export function shouldRedirectOnError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401 || error.status === 403
  }
  return false
}

// Función helper para obtener el código de estado HTTP de un error
export function getErrorStatus(error: unknown): number | null {
  if (error instanceof ApiError) {
    return error.status
  }
  return null
}
